import {
  FeasibilityQuota,
  FeasibilityQuotaGroup,
  FeasibilityQuotaGroupKey,
  FeasibilityQuotaRow,
  FeasibilityQuotaSet,
  NESTED_QUOTA_LABEL,
} from '@ps/survey-utils';
import { Option } from '@sapphire/result';
import { FeasibilityAnalysis } from './feasibility-analysis';
import { formatFeasibilityQuotaLabel } from './feasibility-condition.formatter';

/**
 * quotaSet x FeasibilityAnalysis -> FeasibilityQuotaGroup[]
 */
export class FeasibilityQuotaMapper {
  constructor(private readonly _analysis: FeasibilityAnalysis) {}

  public map(quotaSet: FeasibilityQuotaSet): FeasibilityQuotaGroup[] {
    return [...quotaSet.entries()].map(([key, rows]) =>
      this._mapGroup(key, rows),
    );
  }

  private _mapGroup(
    key: FeasibilityQuotaGroupKey,
    rows: readonly FeasibilityQuota[],
  ): FeasibilityQuotaGroup {
    const firstRow = Option.from(rows[0]);
    const qualificationCode = firstRow.mapOr(0, (row) => row.qualificationCode);
    const name = firstRow
      .filter((row) => (row.criteria?.length ?? 0) > 1)
      .match({
        some: () => this._quotaName(key),
        none: () => firstRow.mapOr('', (row) => row.qualificationName ?? ''),
      });

    return {
      name,
      qualificationCode,
      quotas: rows.map((row) => this._mapQuota(key, row)),
      hasPopulationData: this._analysis.hasPopulationData(qualificationCode),
    };
  }

  private _mapQuota(
    groupKey: FeasibilityQuotaGroupKey,
    row: FeasibilityQuota,
  ): FeasibilityQuotaRow {
    const isUnknown = this._analysis.isUnknown(row.qualificationCode, row);
    const feasible = this._analysis.count(row.buyerQuotaId);

    return {
      buyerQuotaId: row.buyerQuotaId,
      groupKey: String(groupKey),
      label: formatFeasibilityQuotaLabel(row, this._analysis.currencySymbol),
      desired: row.requiredCount,
      estimate: isUnknown
        ? { kind: 'unknown' }
        : {
            kind: 'estimated',
            feasible,
            difference: feasible - row.requiredCount,
            isFeasible: feasible >= row.requiredCount,
          },
      isDifficult: this._analysis.isDifficult(row.buyerQuotaId),
    };
  }

  private _quotaName(key: FeasibilityQuotaGroupKey): string {
    return key !== NESTED_QUOTA_LABEL ? String(key) : 'Autonested';
  }
}
